import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, IconButton, Typography, useMediaQuery } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

export interface TrustpilotReview {
  stars: number;
  title: string;
  message: string;
  author: string;
  linkUrl: string;
}

export interface TrustpilotReviewSliderProps {
  reviews: TrustpilotReview[];
}

const MOBILE_CARD_GAP = 24;
const DESKTOP_CARD_GAP = 48;
const DESKTOP_CARD_WIDTH = 380;
const DESKTOP_VISIBLE_COUNT = 3;
const MOBILE_CONTAINER_PADDING = 48;

function StarRating({ count }: { count: number }) {
  const filled = Math.min(5, Math.max(0, Math.round(count)));
  return (
    <Box sx={{ display: "flex", gap: "3px", mb: 2 }}>
      {Array.from({ length: 5 }, (_, i) => (
        <Box
          key={i}
          component="span"
          sx={{
            fontSize: "28px",
            color: i < filled ? "#f4a72c" : "#c8c8c8",
            lineHeight: 1,
          }}
        >
          ★
        </Box>
      ))}
    </Box>
  );
}

/**
 * A carousel of Trustpilot review cards.
 * Desktop shows 3 cards at once; mobile shows 1.
 * Navigate with prev/next buttons.
 */
export default function TrustpilotReviewSlider({
  reviews,
}: TrustpilotReviewSliderProps) {
  const isDesktop = useMediaQuery("(min-width: 900px)");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [mobileCardWidth, setMobileCardWidth] = useState(280);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateMobileCardWidth = () => {
      if (containerRef.current) {
        setMobileCardWidth(
          containerRef.current.offsetWidth - MOBILE_CONTAINER_PADDING
        );
      }
    };
    updateMobileCardWidth();
    window.addEventListener("resize", updateMobileCardWidth);
    return () => window.removeEventListener("resize", updateMobileCardWidth);
  }, []);

  const visibleCount = isDesktop ? DESKTOP_VISIBLE_COUNT : 1;
  const maxIndex = Math.max(0, (reviews?.length ?? 0) - visibleCount);
  const cardGap = isDesktop ? DESKTOP_CARD_GAP : MOBILE_CARD_GAP;
  const cardWidth = isDesktop ? DESKTOP_CARD_WIDTH : mobileCardWidth;
  const cardStep = cardWidth + cardGap;

  const goTo = useCallback(
    (index: number) => {
      setCurrentIndex(Math.min(maxIndex, Math.max(0, index)));
    },
    [maxIndex]
  );

  const handlePrev = useCallback(
    () => goTo(currentIndex - 1),
    [currentIndex, goTo]
  );
  const handleNext = useCallback(
    () => goTo(currentIndex + 1),
    [currentIndex, goTo]
  );

  if (!reviews || reviews.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        Add review entries in Builder.io
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{ py: { xs: 4, md: 6 }, overflow: "hidden", width: "100%" }}
    >
      {/* Card track */}
      <Box
        sx={{
          display: "flex",
          gap: `${cardGap}px`,
          pl: { xs: 4, md: 8 },
          transform: `translateX(-${currentIndex * cardStep}px)`,
          transition: "transform 0.35s ease",
        }}
      >
        {reviews.map((review, index) => (
          <Box
            key={index}
            component="a"
            href={review.linkUrl}
            target="_blank"
            rel="noreferrer"
            sx={{
              textDecoration: "none",
              color: "inherit",
              flexShrink: 0,
              width: `${cardWidth}px`,
              backgroundColor: "#fff",
              borderRadius: "20px",
              pt: 5,
              px: 5,
              pb: 8,
              display: "flex",
              flexDirection: "column",
              minHeight: "260px",
              boxSizing: "border-box",
            }}
          >
            <StarRating count={review.stars} />
            <Typography
              sx={{
                fontFamily: "poppins, sans-serif",
                fontWeight: 700,
                mb: 3,
                color: "rgba(48, 58, 82, 1)",
                fontSize: "18px",
              }}
            >
              {review.title}
            </Typography>
            <Typography
              sx={{
                fontFamily: "poppins, sans-serif",
                fontWeight: 500,
                fontSize: "16px",
                color: "rgba(48, 58, 82, 1)",
                flex: 1,
                mb: 2.5,
                lineHeight: 1.6,
              }}
            >
              {review.message}
            </Typography>
            <Typography
              sx={{ fontSize: "14px", fontWeight: 400, color: "#1a1a1a" }}
            >
              {review.author}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Navigation buttons — goTo clamps to valid range */}
      <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 4 }}>
        <IconButton
          onClick={handlePrev}
          aria-label="Previous reviews"
          sx={{
            border: "2px solid rgba(255,255,255,0.7)",
            color: "white",
            width: 48,
            height: 48,
            "&:hover": { backgroundColor: "rgba(255,255,255,0.15)" },
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <IconButton
          onClick={handleNext}
          aria-label="Next reviews"
          sx={{
            border: "2px solid rgba(255,255,255,0.7)",
            color: "white",
            width: 48,
            height: 48,
            "&:hover": { backgroundColor: "rgba(255,255,255,0.15)" },
          }}
        >
          <ArrowForwardIcon />
        </IconButton>
      </Box>
    </Box>
  );
}
